import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import api, { API_BASE } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Lock, ArrowLeft, ShoppingCart, Tag, CheckCircle, Loader2, X, Sparkles } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { getDisplayPrice } from '@/lib/priceUtils';
import { PriceDisplay } from '@/components/PriceDisplay';
import { getReferralCode } from '@/hooks/useReferral';

export default function Checkout() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const productId = searchParams.get('product_id');

  const [product, setProduct] = useState(null);
  const [email, setEmail] = useState('');
  const [offers, setOffers] = useState([]);
  const [selectedOffers, setSelectedOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  useEffect(() => {
    if (!productId) { navigate('/'); return; }
    Promise.all([
      api.get('/public/products').then(r => {
        const p = r.data.find(pr => pr.id === productId);
        setProduct(p);
        // Meta Pixel: InitiateCheckout
        if (p && window.fbq) window.fbq('track', 'InitiateCheckout', { content_ids: [p.id], content_name: p.title, value: p.discounted_price ?? p.price, currency: 'USD' });
      }),
      api.get(`/public/offers/product/${productId}`).then(r => setOffers(r.data || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [productId, navigate]);

  if (loading || !product) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#1A9DA5] animate-spin" /></div>;

  const { hasDiscount, isFree, discounted, original } = getDisplayPrice(product);
  const basePrice = hasDiscount ? (isFree ? 0 : discounted) : original;
  const offersTotal = selectedOffers.reduce((sum, oid) => {
    const o = offers.find(of => of.id === oid);
    return sum + (o ? parseFloat(o.price || 0) : 0);
  }, 0);
  const subtotal = basePrice + offersTotal;
  const couponDiscount = appliedCoupon ? appliedCoupon.discount : 0;
  const total = Math.max(subtotal - couponDiscount, 0);

  const validateCoupon = async () => {
    if (!couponCode.trim()) return;
    setValidatingCoupon(true);
    try {
      const { data } = await api.post('/public/coupons/validate', {
        code: couponCode.trim(),
        product_id: productId,
        subtotal: subtotal,
      });
      setAppliedCoupon(data);
      toast.success(`Coupon applied: ${data.type === 'percentage' ? `${data.value}% off` : `$${data.value} off`}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid coupon');
      setAppliedCoupon(null);
    }
    setValidatingCoupon(false);
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
  };

  const handleCheckout = async () => {
    if (!email.trim()) { toast.error('Please enter your email'); return; }
    setProcessing(true);
    try {
      const { data } = await api.post('/checkout/create-session', {
        product_id: productId,
        email: email.trim(),
        offer_ids: selectedOffers,
        origin_url: window.location.origin,
        coupon_code: appliedCoupon?.code || '',
        referral_code: getReferralCode(),
      });
      if (data.free) {
        toast.success('Access granted! Redirecting...');
        window.location.href = data.url;
      } else if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout failed');
    }
    setProcessing(false);
  };

  const toggleOffer = (oid) => setSelectedOffers(prev => prev.includes(oid) ? prev.filter(id => id !== oid) : [...prev, oid]);

  return (
    <div className="min-h-screen bg-background" data-testid="checkout-page">
      <header className="border-b bg-card">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><img src="/logo.png" alt="Wayfinder" className="w-6 h-6 rounded" /><span className="font-bold font-['Outfit']">Wayfinder</span></Link>
          <div className="flex items-center gap-1 text-sm text-muted-foreground"><Lock className="w-3.5 h-3.5" />Secure Checkout</div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-10">
        <Button variant="ghost" className="mb-6" onClick={() => navigate(-1)} data-testid="checkout-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" />Back
        </Button>

        <div className="grid md:grid-cols-5 gap-8">
          {/* Order Summary */}
          <div className="md:col-span-2 order-2 md:order-1">
            <Card>
              <CardContent className="p-5 space-y-4">
                <h3 className="font-semibold font-['Outfit'] text-lg">Order Summary</h3>

                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-medium text-sm">{product.title}</p>
                    <p className="text-xs text-muted-foreground">{product.type === 'package' ? 'Package' : 'Audio Guide'}</p>
                  </div>
                  <div className="text-right">
                    {hasDiscount ? (
                      <div>
                        <p className="text-sm line-through text-muted-foreground">${original}</p>
                        <p className="font-bold text-[#E8735A]">{isFree ? 'FREE' : `$${discounted.toFixed(2)}`}</p>
                      </div>
                    ) : (
                      <p className="font-bold">${original}</p>
                    )}
                  </div>
                </div>

                {selectedOffers.length > 0 && (
                  <>
                    <Separator />
                    {selectedOffers.map(oid => {
                      const o = offers.find(of => of.id === oid);
                      return o ? (
                        <div key={oid} className="flex justify-between text-sm">
                          <span className="text-muted-foreground">+ {o.name}</span>
                          <span>${o.price}</span>
                        </div>
                      ) : null;
                    })}
                  </>
                )}

                <Separator />

                {/* Coupon Code */}
                <div className="space-y-2" data-testid="coupon-section">
                  <p className="text-sm font-medium flex items-center gap-1"><Tag className="w-3.5 h-3.5 text-[#1A9DA5]" />Coupon Code</p>
                  {appliedCoupon ? (
                    <div className="flex items-center gap-2 p-2 rounded-lg bg-[#1A9DA5]/5 border border-[#1A9DA5]/20">
                      <CheckCircle className="w-4 h-4 text-[#1A9DA5]" />
                      <span className="font-mono text-sm font-bold flex-1">{appliedCoupon.code}</span>
                      <Badge className="bg-[#1A9DA5]/10 text-[#1A9DA5]">-${appliedCoupon.discount.toFixed(2)}</Badge>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={removeCoupon} data-testid="remove-coupon-btn"><X className="w-3 h-3" /></Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        value={couponCode}
                        onChange={e => setCouponCode(e.target.value.toUpperCase())}
                        placeholder="Enter code"
                        className="font-mono uppercase text-sm"
                        onKeyDown={e => e.key === 'Enter' && validateCoupon()}
                        data-testid="coupon-input"
                      />
                      <Button variant="outline" size="sm" onClick={validateCoupon} disabled={validatingCoupon || !couponCode.trim()} data-testid="apply-coupon-btn">
                        {validatingCoupon ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Apply'}
                      </Button>
                    </div>
                  )}
                </div>

                <Separator />

                {couponDiscount > 0 && (
                  <div className="flex justify-between text-sm text-[#1A9DA5]">
                    <span>Coupon discount</span>
                    <span>-${couponDiscount.toFixed(2)}</span>
                  </div>
                )}

                <div className="flex justify-between items-center font-bold text-lg">
                  <span>Total</span>
                  <span className={total === 0 ? 'text-[#1A9DA5]' : ''}>{total === 0 ? 'FREE' : `$${total.toFixed(2)}`}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Checkout Form */}
          <div className="md:col-span-3 order-1 md:order-2 space-y-4">
            <h1 className="text-2xl font-bold font-['Outfit']">Complete Your Order</h1>

            {isFree && !appliedCoupon && (
              <div className="p-4 rounded-lg bg-[#1A9DA5]/5 border border-[#1A9DA5]/20 flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-[#1A9DA5] shrink-0" />
                <div>
                  <p className="font-semibold text-sm">This guide is free!</p>
                  <p className="text-xs text-muted-foreground">Just enter your email and get instant access.</p>
                </div>
              </div>
            )}

            {total === 0 && appliedCoupon && (
              <div className="p-4 rounded-lg bg-[#1A9DA5]/5 border border-[#1A9DA5]/20 flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-[#1A9DA5] shrink-0" />
                <div>
                  <p className="font-semibold text-sm">Your coupon makes this free!</p>
                  <p className="text-xs text-muted-foreground">Just enter your email and get instant access.</p>
                </div>
              </div>
            )}

            <div className="space-y-1">
              <label className="text-sm font-medium">Email Address</label>
              <Input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@email.com"
                data-testid="checkout-email-input"
              />
              <p className="text-xs text-muted-foreground">We'll send your access link to this email.</p>
            </div>

            {offers.filter(o => o.type === 'order_bump').map(offer => (
              <Card key={offer.id} className={`border-dashed ${selectedOffers.includes(offer.id) ? 'border-[#1A9DA5] bg-[#1A9DA5]/5' : ''}`}>
                <CardContent className="p-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={selectedOffers.includes(offer.id)} onChange={() => toggleOffer(offer.id)} className="mt-1 accent-[#1A9DA5]" />
                    <div className="flex-1">
                      <p className="font-medium text-sm">{offer.headline || offer.name}</p>
                      <p className="text-xs text-muted-foreground">+${offer.price}</p>
                    </div>
                  </label>
                </CardContent>
              </Card>
            ))}

            <Button
              onClick={handleCheckout}
              disabled={processing || !email.trim()}
              className="w-full bg-[#1A9DA5] hover:bg-[#158D92] text-white py-6 text-lg font-semibold shadow-lg transition-all"
              data-testid="checkout-pay-btn"
            >
              {processing ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <ShoppingCart className="w-5 h-5 mr-2" />}
              {processing ? 'Processing...' : total === 0 ? 'Get Free Access' : `Pay $${total.toFixed(2)}`}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
